import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix default marker icon
delete (L.Icon.Default.prototype as unknown as Record<string, unknown>)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

interface Marker {
  id: string;
  lat: number;
  lng: number;
  name: string;
  selected?: boolean;
  label?: string;
  address?: string;
  photoUrl?: string;
  healthScore?: number | null;
  healthTrend?: 'growing' | 'stable' | 'declining' | null;
  googleRating?: number | null;
  googleReviewCount?: number | null;
  reviewVelocity?: number | null;
  lastEnrichedAt?: string | null;
  placeId?: string | null;
  openingHours?: Record<string, [number, number]> | null;
  website?: string | null;
  phoneNumber?: string | null;
}

function getMarkerSize(score: number): number {
  if (score >= 80) return 24;
  if (score >= 50) return 18;
  return 14;
}

function getMarkerColor(trend: 'growing' | 'stable' | 'declining' | null | undefined): string {
  if (trend === 'growing') return '#22c55e';
  if (trend === 'declining') return '#ef4444';
  return '#f59e0b';
}

function timeAgo(isoString: string): string {
  const diffMs = Date.now() - new Date(isoString).getTime();
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  if (hours < 1) return 'just now';
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function trendIcon(trend: 'growing' | 'stable' | 'declining' | null | undefined): string {
  if (trend === 'growing') return '↑';
  if (trend === 'declining') return '↓';
  return '→';
}

function healthScoreColor(score: number): string {
  if (score >= 80) return '#16a34a';
  if (score >= 60) return '#65a30d';
  if (score >= 40) return '#ca8a04';
  return '#dc2626';
}


interface Props {
  markers?: Marker[];
  center?: { lat: number; lng: number };
  zoom?: number;
  onMarkerClick?: (id: string) => void;
  showRoute?: boolean;
  routePoints?: Array<{ lat: number; lng: number }>;
  showBoundary?: boolean;
  showLocationButton?: boolean;
  autoLocate?: boolean;
  fitToMarkers?: boolean;
  onLocationFound?: (location: { lat: number; lng: number }) => void;
}

// Calculate convex hull using Graham scan
function convexHull(points: Array<{ lat: number; lng: number }>): Array<{ lat: number; lng: number }> {
  if (points.length < 3) return points;

  const sorted = [...points].sort((a, b) => a.lng - b.lng || a.lat - b.lat);

  const cross = (o: { lat: number; lng: number }, a: { lat: number; lng: number }, b: { lat: number; lng: number }) =>
    (a.lng - o.lng) * (b.lat - o.lat) - (a.lat - o.lat) * (b.lng - o.lng);

  const lower: Array<{ lat: number; lng: number }> = [];
  for (const p of sorted) {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) lower.pop();
    lower.push(p);
  }

  const upper: Array<{ lat: number; lng: number }> = [];
  for (let i = sorted.length - 1; i >= 0; i--) {
    const p = sorted[i];
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) upper.pop();
    upper.push(p);
  }

  lower.pop();
  upper.pop();
  return lower.concat(upper);
}

export function Map({
  markers = [],
  center,
  zoom = 12,
  onMarkerClick,
  showRoute = false,
  routePoints = [],
  showBoundary = false,
  showLocationButton = true,
  autoLocate = false,
  fitToMarkers = true,
  onLocationFound,
}: Props) {
  const mapRef = useRef<L.Map | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const markersRef = useRef<L.Marker[]>([]);
  const routeRef = useRef<L.Polyline | null>(null);
  const boundaryRef = useRef<L.Polygon | null>(null);
  const locationMarkerRef = useRef<L.Marker | null>(null);
  const locationCircleRef = useRef<L.Circle | null>(null);
  const hasAutoLocated = useRef(false);
  const prevMarkersLength = useRef(0);

  const [isLocating, setIsLocating] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);

  // Locate user function
  const locateUser = (panToLocation = true) => {
    if (!mapRef.current || !navigator.geolocation) {
      setLocationError('Geolocation not supported');
      return;
    }

    setIsLocating(true);
    setLocationError(null);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude: lat, longitude: lng, accuracy } = position.coords;

        if (!mapRef.current) return;

        // Remove existing location markers
        if (locationMarkerRef.current) {
          locationMarkerRef.current.remove();
        }
        if (locationCircleRef.current) {
          locationCircleRef.current.remove();
        }

        // Add accuracy circle
        locationCircleRef.current = L.circle([lat, lng], {
          radius: Math.min(accuracy, 1000), // Cap at 1km for visual clarity
          color: '#3b82f6',
          fillColor: '#3b82f6',
          fillOpacity: 0.15,
          weight: 1,
        }).addTo(mapRef.current);

        // Add location marker (pulsing blue dot)
        const locationIcon = L.divIcon({
          className: 'location-marker',
          html: `
            <div class="relative">
              <div class="absolute w-6 h-6 bg-blue-500 rounded-full opacity-30 animate-ping"></div>
              <div class="relative w-4 h-4 bg-blue-600 rounded-full border-2 border-white shadow-lg" style="margin: 4px;"></div>
            </div>
          `,
          iconSize: [24, 24],
          iconAnchor: [12, 12],
        });

        locationMarkerRef.current = L.marker([lat, lng], { icon: locationIcon })
          .addTo(mapRef.current)
          .bindPopup('You are here');

        // Pan to location if requested
        if (panToLocation) {
          mapRef.current.setView([lat, lng], 13);
        }

        setIsLocating(false);
        onLocationFound?.({ lat, lng });
      },
      (error) => {
        setIsLocating(false);
        switch (error.code) {
          case error.PERMISSION_DENIED:
            setLocationError('Location access denied');
            break;
          case error.POSITION_UNAVAILABLE:
            setLocationError('Location unavailable');
            break;
          case error.TIMEOUT:
            setLocationError('Location request timed out');
            break;
          default:
            setLocationError('Failed to get location');
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000,
      }
    );
  };

  // Initialize map
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const defaultCenter: [number, number] = center
      ? [center.lat, center.lng]
      : [39.8283, -98.5795]; // Center of US

    mapRef.current = L.map(containerRef.current, {
      zoomControl: false, // Disable default top-left zoom control
    }).setView(defaultCenter, zoom);

    // Add zoom control to top-left to avoid conflict with mobile search bar at bottom
    L.control.zoom({ position: 'topleft' }).addTo(mapRef.current);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
    }).addTo(mapRef.current);

    // Auto-locate on mount if enabled
    if (autoLocate && !hasAutoLocated.current) {
      hasAutoLocated.current = true;
      // Small delay to ensure map is ready
      setTimeout(() => locateUser(true), 500);
    }

    return () => {
      mapRef.current?.remove();
      mapRef.current = null;
    };
  }, []);

  // Update markers
  useEffect(() => {
    if (!mapRef.current) return;

    // Clear existing markers
    markersRef.current.forEach((m) => m.remove());
    markersRef.current = [];

    // Add new markers
    markers.forEach((marker) => {
      let icon: L.DivIcon;
      const hasHealth = marker.healthScore != null;

      if (marker.label) {
        icon = L.divIcon({
          className: 'custom-marker',
          html: `<div class="flex items-center justify-center w-8 h-8 bg-blue-600 text-white rounded-full font-bold text-sm shadow-lg">${marker.label}</div>`,
        });
      } else if (marker.selected) {
        icon = L.divIcon({
          className: 'custom-marker',
          html: `<div class="w-5 h-5 bg-red-500 rounded-full border-3 border-white shadow-lg ring-2 ring-red-300"></div>`,
          iconSize: [20, 20],
          iconAnchor: [10, 10],
        });
      } else if (hasHealth) {
        const size = getMarkerSize(marker.healthScore!);
        const color = getMarkerColor(marker.healthTrend);
        const isDeclining = marker.healthTrend === 'declining';
        icon = L.divIcon({
          className: `health-marker${isDeclining ? ' health-marker-pulse' : ''}`,
          html: `<div style="width:${size}px;height:${size}px;background:${color};border-radius:50%;border:2px solid white;box-shadow:0 2px 4px rgba(0,0,0,0.3);"></div>`,
          iconSize: [size, size],
          iconAnchor: [size / 2, size / 2],
        });
      } else {
        icon = L.divIcon({
          className: 'custom-marker',
          html: '<div class="w-4 h-4 bg-blue-600 rounded-full border-2 border-white shadow-lg"></div>',
          iconSize: [16, 16],
          iconAnchor: [8, 8],
        });
      }

      const noHealthSection = !hasHealth && marker.placeId
        ? `<div style="margin-top:6px;padding-top:6px;border-top:1px solid #e5e7eb;font-size:10px;color:#9ca3af;">No health data yet — click Refresh in store details</div>`
        : '';

      const healthSection = hasHealth ? `
        <div style="margin-top:8px;padding-top:8px;border-top:1px solid #e5e7eb;">
          <div style="display:flex;align-items:center;gap:6px;margin-bottom:4px;">
            <span style="font-size:18px;font-weight:700;color:${healthScoreColor(marker.healthScore!)};">${marker.healthScore}</span>
            <span style="font-size:11px;color:#6b7280;">health score</span>
            <span style="margin-left:auto;font-size:13px;font-weight:600;color:${
              marker.healthTrend === 'growing' ? '#16a34a' : marker.healthTrend === 'declining' ? '#dc2626' : '#78716c'
            };">${trendIcon(marker.healthTrend)}</span>
          </div>
          ${marker.googleRating != null ? `
            <div style="display:flex;align-items:center;gap:4px;font-size:11px;color:#374151;margin-bottom:2px;">
              <span style="color:#f59e0b;">★</span>
              <span>${marker.googleRating.toFixed(1)}</span>
              ${marker.googleReviewCount != null ? `<span style="color:#9ca3af;">(${marker.googleReviewCount.toLocaleString()})</span>` : ''}
              ${marker.reviewVelocity != null && marker.reviewVelocity > 0 ? `<span style="color:#6b7280;margin-left:4px;">${marker.reviewVelocity}/mo</span>` : ''}
            </div>
          ` : ''}
          ${marker.lastEnrichedAt ? `<div style="font-size:10px;color:#9ca3af;margin-top:2px;">Updated ${timeAgo(marker.lastEnrichedAt)}</div>` : ''}
        </div>
      ` : '';

      const popupContent = `
        <div style="min-width:160px;max-width:220px;">
          <div style="padding:2px 2px 4px;">
            <h3 style="font-weight:600;color:#111827;font-size:13px;line-height:1.3;margin:0 0 2px;">${marker.name}</h3>
            ${marker.address ? `<p style="font-size:11px;color:#6b7280;margin:0 0 4px;line-height:1.4;">${marker.address}</p>` : ''}
            ${healthSection}
            ${noHealthSection}
          </div>
        </div>
      `;

      const m = L.marker([marker.lat, marker.lng], { icon })
        .addTo(mapRef.current!)
        .bindPopup(popupContent, { className: 'custom-popup', maxWidth: 300 });

      if (onMarkerClick) {
        m.on('click', () => onMarkerClick(marker.id));
      }

      markersRef.current.push(m);
    });

    // Fit bounds when markers change (new search results)
    if (fitToMarkers && markers.length > 0 && markers.length !== prevMarkersLength.current) {
      const bounds = L.latLngBounds(markers.map((m) => [m.lat, m.lng]));
      mapRef.current.fitBounds(bounds, { padding: [60, 60], maxZoom: 14 });
    }
    prevMarkersLength.current = markers.length;
  }, [markers, onMarkerClick, fitToMarkers]);

  // Update center when a specific marker is selected
  useEffect(() => {
    if (!mapRef.current || !center) return;
    mapRef.current.setView([center.lat, center.lng], 15, { animate: true });
  }, [center]);

  // Update route
  useEffect(() => {
    if (!mapRef.current) return;

    if (routeRef.current) {
      routeRef.current.remove();
      routeRef.current = null;
    }

    if (showRoute && routePoints.length > 1) {
      routeRef.current = L.polyline(
        routePoints.map((p) => [p.lat, p.lng]),
        { color: '#2563eb', weight: 4, opacity: 0.7 }
      ).addTo(mapRef.current);
    }
  }, [showRoute, routePoints]);

  // Update territory boundary
  useEffect(() => {
    if (!mapRef.current) return;

    if (boundaryRef.current) {
      boundaryRef.current.remove();
      boundaryRef.current = null;
    }

    if (showBoundary && markers.length >= 3) {
      const hull = convexHull(markers.map((m) => ({ lat: m.lat, lng: m.lng })));
      if (hull.length >= 3) {
        boundaryRef.current = L.polygon(
          hull.map((p) => [p.lat, p.lng]),
          { color: '#10b981', weight: 2, opacity: 0.6, fillOpacity: 0.1 }
        ).addTo(mapRef.current);
      }
    }
  }, [showBoundary, markers]);

  // Clear location error after 3 seconds
  useEffect(() => {
    if (locationError) {
      const timer = setTimeout(() => setLocationError(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [locationError]);

  return (
    <div className="relative h-full w-full">
      <div ref={containerRef} className="h-full w-full" />

      {/* Locate Me Button */}
      {showLocationButton && (
        <button
          onClick={() => locateUser(true)}
          disabled={isLocating}
          className="absolute top-4 right-4 z-[1000] bg-white rounded-lg shadow-lg p-3 hover:bg-gray-50 disabled:opacity-50 transition-all"
          title="Find my location"
        >
          {isLocating ? (
            <svg className="w-5 h-5 text-blue-600 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          ) : (
            <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          )}
        </button>
      )}

      {/* Error toast */}
      {locationError && (
        <div className="absolute bottom-16 right-4 z-[1000] bg-red-50 text-red-700 px-4 py-2 rounded-lg shadow-lg text-sm animate-fade-in">
          {locationError}
        </div>
      )}
    </div>
  );
}
